import { reportsBuilderApi } from '../../services/reportsBuilderService';
import { buildQueryPayload } from './builderValidation';

const STORAGE_KEY = 'manual_report_builder_reports_index_v3';

const nowIso = () => new Date().toISOString();

const createLocalId = () => `local_report_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const readItems = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error parsing reports index:', error);
    return [];
  }
};

const writeItems = (items) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
};

const upsertLocalIndex = (item) => {
  const items = readItems();
  const idx = items.findIndex((candidate) => candidate.id === item.id);
  if (idx >= 0) {
    items[idx] = {
      ...items[idx],
      ...item,
      updatedAt: item.updatedAt || nowIso(),
    };
  } else {
    items.push(item);
  }
  writeItems(items);
};

const normalizeDefinitionFromApi = (definition) => ({
  dimensions: Array.isArray(definition?.dimensions) ? definition.dimensions : [],
  metrics: (Array.isArray(definition?.metrics) ? definition.metrics : []).map((metric) => ({
    ...metric,
    aggregation: metric?.aggregate || metric?.aggregation || 'count',
  })),
  filters: (Array.isArray(definition?.filters) ? definition.filters : []).map((filter) => {
    const values = Array.isArray(filter?.values) ? filter.values : [];
    if (values.length) return { ...filter, values };
    if (filter?.valueTo !== undefined) return { ...filter, values: [filter.value, filter.valueTo] };
    if (filter?.value !== undefined) return { ...filter, values: [filter.value] };
    return { ...filter, values: [] };
  }),
  sort: Array.isArray(definition?.sort) ? definition.sort : [],
});

const normalizeSavedReport = (payload) => ({
  id: String(payload?.id || payload?.reportId || payload?.reportDefinitionId || createLocalId()),
  name: String(payload?.name || ''),
  description: String(payload?.description || ''),
  createdAt: payload?.createdAt || payload?.create_At || nowIso(),
  updatedAt: payload?.updatedAt || payload?.update_At || nowIso(),
  definition: normalizeDefinitionFromApi(payload?.definition),
});

const normalizeSavedReportListItem = (payload) => ({
  id: String(payload?.id || payload?.reportId || payload?.reportDefinitionId || createLocalId()),
  name: String(payload?.name || ''),
  description: String(payload?.description || ''),
  createdAt: payload?.createdAt || payload?.create_At || nowIso(),
  updatedAt: payload?.updatedAt || payload?.update_At || nowIso(),
});

export const reportStorageService = {
  async list({ page = 1, pageSize = 20, search = '' } = {}) {
    const safePage = Number(page) || 1;
    const safePageSize = Math.min(Number(pageSize) || 20, 100);
    const searchText = String(search || '').trim().toLowerCase();

    try {
      const response = await reportsBuilderApi.listReports({
        page: safePage,
        pageSize: safePageSize,
        search: searchText,
      });

      const items = Array.isArray(response?.items)
        ? response.items.map(normalizeSavedReportListItem)
        : [];

      items.forEach((item) => {
        upsertLocalIndex(item);
      });

      return {
        page: Number(response?.page || safePage),
        pageSize: Number(response?.pageSize || safePageSize),
        total: Number(response?.total || items.length),
        items,
      };
    } catch (error) {
      const localItems = readItems()
        .filter((item) => {
          if (!searchText) return true;
          return (
            String(item?.name || '')
              .toLowerCase()
              .includes(searchText) ||
            String(item?.description || '')
              .toLowerCase()
              .includes(searchText)
          );
        })
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

      const start = (safePage - 1) * safePageSize;
      const paged = localItems.slice(start, start + safePageSize);

      return {
        page: safePage,
        pageSize: safePageSize,
        total: localItems.length,
        items: paged.map(normalizeSavedReportListItem),
      };
    }
  },

  async save(definition, operatorCatalog) {
    const payload = {
      name: String(definition?.name || '').trim(),
      description: String(definition?.description || '').trim(),
      isShared: false,
      definition: buildQueryPayload(definition, operatorCatalog),
    };

    const response = await reportsBuilderApi.saveReport(payload);
    const saved = normalizeSavedReport({
      ...response,
      name: response?.name || payload.name,
      description: response?.description || payload.description,
      definition: response?.definition || payload.definition,
    });

    upsertLocalIndex({
      id: saved.id,
      name: saved.name,
      description: saved.description,
      createdAt: saved.createdAt,
      updatedAt: saved.updatedAt,
      definition: saved.definition,
    });

    return saved;
  },

  async load(reportId) {
    try {
      const response = await reportsBuilderApi.getReportById(reportId);
      const report = normalizeSavedReport(response);

      upsertLocalIndex({
        id: report.id,
        name: report.name,
        description: report.description,
        createdAt: report.createdAt,
        updatedAt: report.updatedAt,
        definition: report.definition,
      });

      return report;
    } catch (error) {
      const localFallback = readItems().find((item) => item.id === reportId && item.definition);
      if (localFallback) {
        return {
          id: String(localFallback.id),
          name: String(localFallback.name || ''),
          description: String(localFallback.description || ''),
          createdAt: localFallback.createdAt || nowIso(),
          updatedAt: localFallback.updatedAt || nowIso(),
          definition: normalizeDefinitionFromApi(localFallback.definition),
        };
      }
      throw error;
    }
  },
};

export default reportStorageService;
